import { useMemo } from "react";
import { Service as BaseService, useService as useBaseService } from "./Base";

export enum SubscriptionPlan {
    BASIC = "basic",
    PRO = "pro",
    /* ULTRA = "ultra", */
}

export type SubscriptionPlanType = `${SubscriptionPlan}`;

interface Subscription {
    id: string;
    status: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
    priceId: string;
    productId: string;
}

interface SubscriptionResponse {
    subscription: Subscription | null;
    plan: SubscriptionPlan | null;
    hasUnlimitedAccess?: boolean;
    characterLimit?: number | null;
    charactersUsed?: number;
}

export interface PlanMetadata {
    id: SubscriptionPlan;
    name: string;
    description: string;
    price: string;
    features: string[];
    tier: number;
    disabled: boolean;
    characterLimit: number;
}

interface PlansResponse {
    plans: PlanMetadata[];
    currentPlan: SubscriptionPlan | null;
    hasUnlimitedAccess?: boolean;
    characterLimit?: number | null;
    charactersUsed?: number;
}

interface CreateCheckoutSessionParams {
    plan: SubscriptionPlan;
    successUrl: string;
    cancelUrl: string;
}

interface CreateCheckoutSessionResponse {
    url: string;
    sessionId: string;
}

interface CreatePortalSessionParams {
    returnUrl: string;
}

interface CreatePortalSessionResponse {
    url: string;
}

interface UpdateSubscriptionPlanParams {
    plan: SubscriptionPlan;
}

interface UpdateSubscriptionPlanResponse {
    subscription: Subscription;
}

export class Service {
    base: BaseService;
    route: string;

    constructor(service: BaseService, route: string) {
        this.base = service;
        this.route = route;
    }

    async getSubscription(): Promise<SubscriptionResponse> {
        return await this.base.fetchAPI<SubscriptionResponse>(
            `${this.route}/subscription`,
            {
                method: "GET",
            },
        );
    }

    async createCheckoutSession(
        data: CreateCheckoutSessionParams,
    ): Promise<CreateCheckoutSessionResponse> {
        return await this.base.fetchAPI<CreateCheckoutSessionResponse>(
            `${this.route}/checkout`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(data),
            },
        );
    }

    async createPortalSession(
        data: CreatePortalSessionParams,
    ): Promise<CreatePortalSessionResponse> {
        return await this.base.fetchAPI<CreatePortalSessionResponse>(
            `${this.route}/portal`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(data),
            },
        );
    }

    async getPlans(): Promise<PlansResponse> {
        return await this.base.fetchAPI<PlansResponse>(`${this.route}/plans`, {
            method: "GET",
        });
    }

    async updateSubscriptionPlan(
        data: UpdateSubscriptionPlanParams,
    ): Promise<UpdateSubscriptionPlanResponse> {
        return await this.base.fetchAPI<UpdateSubscriptionPlanResponse>(
            `${this.route}/update-plan`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(data),
            },
        );
    }
}

export const useService = () => {
    const base = useBaseService();

    return useMemo(() => new Service(base, "/billing"), [base]);
};
